import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import axios from 'axios';
import DarkLogo from '../../app/common/DarkLogo';
import { getUserData, getUser, getToken } from '../../app/services/AuthService';
import { getApiKey, getUrl } from '../../app/api/agent';
import SecondaryPersonCard from './SecondaryPersonCard';
import MoResultVideo from './MoResultVideo';
import './MoTestKitForm.css';

const MO_SCORE_OPTIONS = [0, 1, 2, 3, 4, 5, 6, 7];

export default function MoTestKitForm() {
  const email = getUser();

  const [ntaScore, setNtaScore] = useState(null);
  const [ntaLoading, setNtaLoading] = useState(true);
  const [moScore, setMoScore] = useState('');
  const [hasMultipleKits, setHasMultipleKits] = useState(false);
  const [secondaries, setSecondaries] = useState([]);
  const [formState, setFormState] = useState('form'); // form | submitting | video | error
  const [videoId, setVideoId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [booking, setBooking] = useState({ loading: true, data: null });

  // Load NTA score: check sessionStorage first, fallback to API
  useEffect(() => {
    const userData = getUserData();
    if (userData?.ntq_score) {
      setNtaScore(userData.ntq_score);
      setNtaLoading(false);
      return;
    }

    if (!email) {
      setNtaLoading(false);
      return;
    }

    const token = getToken();
    axios
      .get(
        `${getUrl()}/lookup-nta?email=${encodeURIComponent(
          email
        )}&token=${encodeURIComponent(token)}`
      )
      .then((res) => {
        if (res.data?.found && res.data?.ntaScore) {
          setNtaScore(res.data.ntaScore);
        }
      })
      .catch((err) => {
        console.error('Error fetching NTA score:', err);
      })
      .finally(() => setNtaLoading(false));
  }, [email]);

  useEffect(() => {
    if (!email) return;
    axios
      .get(`${getUrl()}/has-booked?email=${email}`)
      .then((res) => {
        const bookings = res.data?.bookings;
        if (bookings && bookings.length > 0) {
          setBooking({ loading: false, data: bookings[0] });
        } else {
          setBooking({ loading: false, data: null });
        }
      })
      .catch(() => setBooking({ loading: false, data: null }));
  }, [email]);

  const handleAddSecondary = () => {
    setSecondaries([
      ...secondaries,
      { email: '', isMinor: false, ntaScore: null, moScore: '' },
    ]);
  };

  const handleSecondaryChange = (index, updated) => {
    const copy = [...secondaries];
    copy[index] = updated;
    setSecondaries(copy);
  };

  const handleRemoveSecondary = (index) => {
    setSecondaries(secondaries.filter((_, i) => i !== index));
  };

  const isFormValid = () => {
    if (moScore === '') return false;
    if (hasMultipleKits) {
      for (const sec of secondaries) {
        if (!sec.email || sec.moScore === '') return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) return;

    setFormState('submitting');
    setErrorMessage('');

    try {
      const payload = {
        email,
        ntaScore: ntaScore || null,
        moScore: Number(moScore),
        secondaries: hasMultipleKits
          ? secondaries.map((s) => ({
              email: s.email,
              isMinor: s.isMinor,
              ntaScore: s.ntaScore || null,
              moScore: Number(s.moScore),
            }))
          : [],
      };

      const config = {
        url: getUrl() + '/save-mo-test-kit',
        method: 'post',
        data: payload,
        headers: {
          'x-api-key': getApiKey(),
          'Content-Type': 'application/json',
        },
      };

      const response = await axios(config);
      setVideoId(response.data.videoId);
      setFormState('video');
    } catch (error) {
      console.error('Error submitting MO Test Kit results:', error);
      const msg =
        error.response?.data?.message || 'An error occurred. Please try again.';
      setErrorMessage(msg);
      setFormState('error');
    }
  };

  // Loading NTA score
  if (ntaLoading) {
    return (
      <div className="mo-test-kit-page">
        <Container maxWidth="sm" sx={{ pt: 4 }}>
          <Box className="mo-test-kit-logo">
            <DarkLogo />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mt: 8,
            }}
          >
            <CircularProgress />
          </Box>
        </Container>
      </div>
    );
  }

  // No NTA score — prompt user to take assessment first
  if (!ntaScore) {
    return (
      <div className="mo-test-kit-page">
        <Container maxWidth="sm" sx={{ pt: 4 }}>
          <Box className="mo-test-kit-logo">
            <DarkLogo />
          </Box>
          <div className="mo-test-kit-no-nta-card">
            <div className="mo-test-kit-no-nta-icon">
              <AssignmentLateOutlinedIcon
                sx={{ fontSize: 36, color: '#92400e' }}
              />
            </div>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#3e4959', mb: 1 }}
            >
              One Step First
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 3, lineHeight: 1.6 }}
            >
              Before submitting your test results, you need to complete your NTA
              assessment. This helps us give you the most accurate results.
            </Typography>
            <Button
              variant="contained"
              href="/nta"
              size="large"
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                px: 4,
                py: 1.2,
              }}
            >
              Complete NTA Assessment
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  // Video result view
  if (formState === 'video' && videoId) {
    return (
      <div className="mo-test-kit-video-page">
        <Container maxWidth="md" sx={{ pt: 4 }}>
          <Box className="mo-test-kit-logo">
            <DarkLogo />
          </Box>
          <div className="mo-test-kit-video-card">
            <div className="mo-test-kit-success-icon">
              <CheckCircleOutlineIcon sx={{ fontSize: 36, color: '#059669' }} />
            </div>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#3e4959', mb: 1 }}
            >
              Your Results
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 0, lineHeight: 1.6 }}
            >
              Thank you for submitting your results. Watch the video below to
              learn what your scores mean.
            </Typography>
            <div className="mo-test-kit-video-wrapper">
              <MoResultVideo videoId={videoId} />
            </div>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mt: 3,
                lineHeight: 1.5,
                fontStyle: 'italic',
              }}
            >
              The information on this website is provided for general
              informational and educational purposes only and is not a
              substitute for professional medical advice, diagnosis, or
              treatment. Always seek the advice of your physician or other
              qualified health care provider with any questions you may have
              regarding a medical condition or before starting any detox, diet,
              exercise, or wellness program.
            </Typography>

            {booking.loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <CircularProgress size={20} />
              </Box>
            ) : booking.data ? (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: '#f0f7ff',
                  borderRadius: '10px',
                  border: '1px solid #bae6fd',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, color: '#3e4959', mb: 0.5 }}
                >
                  Your Upcoming Appointment
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {booking.data.subject && (
                    <>
                      {booking.data.subject}
                      <br />
                    </>
                  )}
                  {booking.data.starting_time &&
                    new Date(booking.data.starting_time).toLocaleString(
                      'en-US',
                      {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      }
                    )}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: '#fefce8',
                  borderRadius: '10px',
                  border: '1px solid #fde68a',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#92400e', lineHeight: 1.6 }}
                >
                  If you can&apos;t find a booking confirmation or are not
                  certain if you booked an appointment with us, email:{' '}
                  <a
                    href="mailto:info@pompaprogram.com"
                    style={{ color: '#92400e' }}
                  >
                    info@pompaprogram.com
                  </a>{' '}
                  or call us at{' '}
                  <a href="tel:+18006917422" style={{ color: '#92400e' }}>
                    +1 800-691-7422
                  </a>
                </Typography>
              </Box>
            )}
          </div>
        </Container>
      </div>
    );
  }

  // Form view
  return (
    <div className="mo-test-kit-page">
      <Container maxWidth="sm" sx={{ pt: 4 }}>
        <Box className="mo-test-kit-logo">
          <DarkLogo />
        </Box>

        <div className="mo-test-kit-card">
          <div className="mo-test-kit-header">
            <div className="mo-test-kit-header-icon">
              <ScienceOutlinedIcon sx={{ fontSize: 28, color: '#326a87' }} />
            </div>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#3e4959', mb: 0.5 }}
            >
              Cellular Inflammation Test
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              className="mo-test-kit-subtitle"
            >
              Enter your test kit score below to receive your personalized
              results video.
            </Typography>
          </div>

          <form onSubmit={handleSubmit} className="mo-test-kit-form">
            <div className="mo-test-kit-nta-badge">
              <Typography
                variant="body2"
                sx={{ color: '#326a87', fontWeight: 500 }}
              >
                Your NTA Score:
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#3e4959', fontWeight: 700 }}
              >
                {ntaScore}
              </Typography>
            </div>

            <FormControl fullWidth required>
              <InputLabel>Cellular Inflammation Test Score</InputLabel>
              <Select
                value={moScore}
                onChange={(e) => setMoScore(e.target.value)}
                label="Cellular Inflammation Test Score"
                sx={{ borderRadius: '10px' }}
              >
                {MO_SCORE_OPTIONS.map((val) => (
                  <MenuItem key={val} value={val}>
                    {val}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider className="mo-test-kit-divider" />

            <FormControlLabel
              control={
                <Checkbox
                  checked={hasMultipleKits}
                  onChange={(e) => {
                    setHasMultipleKits(e.target.checked);
                    if (e.target.checked && secondaries.length === 0) {
                      handleAddSecondary();
                    }
                  }}
                />
              }
              label="I ordered multiple test kits"
            />

            {hasMultipleKits && (
              <div className="mo-test-kit-secondaries">
                {secondaries.map((sec, i) => (
                  <SecondaryPersonCard
                    key={i}
                    index={i}
                    data={sec}
                    onChange={handleSecondaryChange}
                    onRemove={handleRemoveSecondary}
                  />
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddSecondary}
                  className="mo-test-kit-add-btn"
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  + Add another person
                </Button>
              </div>
            )}

            {(formState === 'error' || errorMessage) && (
              <Alert
                severity="error"
                sx={{ borderRadius: '10px' }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setFormState('form');
                      setErrorMessage('');
                    }}
                  >
                    Dismiss
                  </Button>
                }
              >
                {errorMessage}
              </Alert>
            )}

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={formState === 'submitting' || !isFormValid()}
              sx={{
                mt: 1,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                py: 1.4,
              }}
            >
              {formState === 'submitting' ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Submit Results'
              )}
            </Button>
          </form>
        </div>
      </Container>
    </div>
  );
}
